import * as React from "react";
import { Button, Text } from "@uikit";
import { Hotel_Package } from "@kalabo/golfsavers-common";
import UpgradeStar_Icon from "@images/icons/upgrade_star.svg";

import XE from "@components/common/xe";
import {
  getPackagePriceBandForDate,
  getPackageTotalCostForDate,
  getPackageTransferLabel,
} from "@kalabo/golfsavers-common";
import { useBreakpoint } from "use-breakpoint";
import { BREAKPOINTS } from "@utilities/constants";
import dayjs from "dayjs";
import { useApp } from "@store/app";
import { Region } from "@kalabo/golfsavers-common";
import { Tooltip } from "antd";

interface IProps {
  region: Region;
  package: Hotel_Package;
  hotel_slug: string;
}

const HotelPackageItem = ({ region, package: pkg, hotel_slug }: IProps) => {
  const package_date = useApp(($) => $.package_date);
  const display_currency = useApp(($) => $.display_currency);

  const { breakpoint } = useBreakpoint(BREAKPOINTS, "mobile");
  let price_bands = getPackagePriceBandForDate(package_date, pkg);

  const tool_tip_enabled = React.useMemo(() => {
    return ["tablet", "large_tablet"].includes(breakpoint);
  }, [breakpoint]);

  const groupedCourseItems = pkg.courses.reduce((result: any, item) => {
    const { id } = item;

    if (!result[id]) {
      result[id] = { name: item.name.en, count: 0 };
    }

    result[id].count++;

    return result;
  }, {});

  const groupedCourses = Object.values(groupedCourseItems) as any;

  const href = `/packages?country_id=${region.country.id}&region_id=${
    region.region_id
  }&hotel_id=${encodeURIComponent(hotel_slug)}&pkg_id=${encodeURIComponent(
    pkg.slug
  )}&package_date=${encodeURIComponent(
    package_date || dayjs().format("YYYY-MM-DD")
  )}`;

  return (
    <div className="group bg-white flex-col overflow-hidden sm:flex-row flex  border-blue border-[2px] rounded-[6px]">
      <div className="flex flex-col sm:flex-row flex-1 p-5 gap-5">
        <div className="sm:hidden lg:block">
          <div className=" relative">
            <div className="group-hover:bg-[rgba(0,0,0,0.2)] transition-all  absolute inset-0 rounded-[4px]"></div>
            <img
              src={`${process.env.GATSBY_IMAGE_PREFIX}${
                pkg.room.image?.url
              }?aspect_ratio=${
                breakpoint !== "mobile" ? "4:3&width=250" : "5:3&width=500"
              }&quality=50`}
              alt={pkg.room.image?.alt}
              className={`rounded-[4px]`}
            />
          </div>
        </div>
        <div className="flex flex-1 flex-col">
          <a aria-label={pkg.name} href={href} target="_blank">
            <Text
              size={19}
              sizeTablet={21}
              sizeDesktop={21}
              leading={1.3}
              element="h4"
              weight="semibold"
              color={"black90"}
              className="mb-2 group-hover:text-blue group-hover:underline"
            >
              {pkg.name}
            </Text>
          </a>
          <Text
            size={15}
            sizeTablet={15}
            sizeDesktop={15}
            weight="semibold"
            color={"black90"}
            className="mb-[10px]"
          >
            Package includes:
          </Text>
          <div className="flex flex-col gap-2">
            <Text
              size={15}
              sizeTablet={15}
              sizeDesktop={15}
              color={"black90"}
              bullet
              bulletType="check"
              leading={1.5}
            >
              <div>
                {pkg.number_of_nights} night
                {pkg.number_of_nights > 1 ? "s" : ""}{" "}
                {pkg.room?.board_type?.toLowerCase()} in a{" "}
                <Tooltip
                  title={
                    tool_tip_enabled ? (
                      <>
                        {" "}
                        <img
                          src={`${process.env.GATSBY_IMAGE_PREFIX}${
                            pkg.room.image?.url
                          }?aspect_ratio=${
                            breakpoint !== "mobile"
                              ? "4:3&width=250"
                              : "5:3&width=500"
                          }&quality=50`}
                          alt={pkg.room.image?.alt}
                          className={`rounded-[4px]`}
                        />
                      </>
                    ) : (
                      ""
                    )
                  }
                  rootClassName="[&_.ant-tooltip-inner]:!p-0.5"
                >
                  <span className="font-semibold sm:text-blue lg:text-black/90">
                    {pkg?.room?.room_type}
                  </span>{" "}
                </Tooltip>
              </div>
            </Text>

            {groupedCourses.length > 0 && (
              <div>
                <Text
                  size={15}
                  sizeTablet={15}
                  sizeDesktop={15}
                  color={"black90"}
                  bullet
                  bulletType="check"
                  leading={1.5}
                >
                  {groupedCourses[0].count > 1
                    ? `${groupedCourses[0].count} x `
                    : ""}
                  18 Holes at{" "}
                  {groupedCourses.length === 1 ? groupedCourses[0].name : ":"}
                </Text>
                <div className="ml-6 flex flex-col gap-1">
                  {groupedCourses.length > 1 &&
                    groupedCourses.map((course: any, idx: any) => {
                      return (
                        <Text
                          key={idx}
                          size={15}
                          sizeTablet={15}
                          sizeDesktop={15}
                          color={"black90"}
                          bullet
                          leading={1.5}
                        >
                          {course.count > 1 ? `${course.count} x ` : ""}
                          {course.name}
                        </Text>
                      );
                    })}
                </div>
              </div>
            )}
            {getPackageTransferLabel(pkg) && (
              <Text
                size={15}
                sizeTablet={15}
                sizeDesktop={15}
                color={"black90"}
                bullet
                bulletType="check"
                leading={1.5}
              >
                {getPackageTransferLabel(pkg)}
              </Text>
            )}
          </div>

          <div className="p-[5px] bg-grey w-full items-center border border-grey2 self-start mt-3">
            <Text
              size={13}
              sizeTablet={13}
              sizeDesktop={13}
              color={"black90"}
              className=""
            >
              <UpgradeStar_Icon className="inline mr-2" />
              <span className="font-semibold">
                Upgrades + Extras Available!
              </span>{" "}
              <span className="hidden xl:inline">
                Create a tailor-made holiday
              </span>
            </Text>
          </div>
        </div>
      </div>
      <div className=" bg-grey flex items-center px-5 py-3 sm:p-5 sm:max-w-[280px] border-l border-grey3">
        <div className="flex flex-1 sm:flex-col items-center justify-between">
          {breakpoint !== "mobile" && (
            <Text
              size={15}
              sizeTablet={15}
              sizeDesktop={15}
              color={"black60"}
              italic
              leading={1.5}
              className="text-center hidden sm:block"
            >
              Take this trip from just...
            </Text>
          )}
          <div className="flex flex-col py-1">
            <div className="flex sm:gap-1 gap-0.5 items-end sm:justify-center ">
              {getPackageTotalCostForDate(package_date, pkg) === 0 ? (
                <Text
                  size={17}
                  sizeTablet={25}
                  sizeDesktop={25}
                  color={"blue"}
                  weight="semibold"
                  leading={1.3}
                >
                  ENQUIRE
                </Text>
              ) : (
                <>
                  {breakpoint === "mobile" && (
                    <Text
                      size={9}
                      italic
                      color="black60"
                      leading={1.5}
                      className="pb-[1px]"
                    >
                      from
                    </Text>
                  )}
                  <Text
                    size={17}
                    sizeTablet={25}
                    sizeDesktop={25}
                    color={"blue"}
                    weight="semibold"
                    leading={1.3}
                  >
                    <XE
                      simplify
                      base_currency={pkg.currency_code}
                      hide_decimal
                      currency={display_currency.code}
                      value={getPackageTotalCostForDate(package_date, pkg)}
                    />
                  </Text>
                  <div className="pb-[2px]">
                    <Text
                      size={12}
                      sizeTablet={17}
                      sizeDesktop={17}
                      color={"blue"}
                      weight="semibold"
                      leading={1.3}
                    >
                      pp
                    </Text>
                  </div>
                </>
              )}
            </div>
            {breakpoint === "mobile" && (
              <Text
                size={10}
                sizeTablet={10}
                sizeDesktop={10}
                color={"black40"}
                leading={1.3}
                className="text-center"
              >
                Based on {pkg.group_size} golfers{" "}
                {pkg.group_size === 1
                  ? `(single occupancy)`
                  : `sharing
                        ${pkg.group_size / pkg.room.room_basis} rooms`}
              </Text>
            )}
          </div>
          {breakpoint !== "mobile" && (
            <>
              <Text
                size={11}
                sizeTablet={11}
                sizeDesktop={11}
                color={"black60"}
                italic
                leading={1.3}
                className="text-center"
              >
                {getPackageTotalCostForDate(package_date, pkg) === 0 ? (
                  <>
                    Get in touch for a full quote for this package for your
                    selected dates.
                  </>
                ) : (
                  <>
                    Prices are per person based on {pkg.group_size} golfers
                    travelling between{" "}
                    {dayjs(price_bands.from_date).format("DD MMM YYYY")} and{" "}
                    {dayjs(price_bands.to_date).format("DD MMM YYYY")} and{" "}
                    {pkg.group_size === 1
                      ? `each having their own room.`
                      : `sharing
                        ${pkg.group_size / pkg.room.room_basis} rooms.`}{" "}
                    Weekend and peak season surcharges may apply.
                  </>
                )}
              </Text>
            </>
          )}

          <a aria-label={pkg.name} href={href} target="_blank">
            <Button
              onClick={async () => {}}
              size="medium"
              color="green"
              width={breakpoint === "mobile" ? "auto" : "full"}
              text={breakpoint === "mobile" ? "View" : "View this package"}
              className="!m-0 sm:!mt-6 !px-6 hover:bg-darkgreen"
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default HotelPackageItem;
